import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Modal, DatePicker, Select, Space, Typography, Row, Col, message, Spin } from 'antd';
import {FileTextOutlined, CheckCircleOutlined, LoadingOutlined, CloseOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import { makeGetCall, makePostCall, getUserDetails, makeGetCallForDownload } from '../../utils/helper';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useModalities } from '../../hooks/useModalities';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const MISDashboardModal = ({ StyleTabsComponent }) => {
  // --- Filter modal state ---
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [activeReport, setActiveReport] = useState('');

  // --- Status monitor modal state ---
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [misStatus, setMisStatus] = useState<'pending' | 'done' | 'error'>('pending');
  const [requestId, setRequestId] = useState('');
  const [queueId, setQueueId] = useState('');
  const [waitSeconds, setWaitSeconds] = useState(30);
  const [downloadUrl, setDownloadUrl] = useState('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Data for filters ---
  const [centers, setCenters] = useState([]);
  const { modalityOptions } = useModalities();
  const enhancedModalityOptions = [
    { label: 'All Modalities', value: 'ALL' },
    ...modalityOptions,
  ];

  // --- Filter values ---
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'days'), dayjs()]);
  const [selectedCenter, setSelectedCenter] = useState('ALL');
  const [selectedModality, setSelectedModality] = useState('ALL');
  const [selectedRadiologist, setSelectedRadiologist] = useState('ALL');
  const [selectedVisitType, setSelectedVisitType] = useState('ALL');
  const [selectedModalityLocation, setSelectedModalityLocation] = useState('ALL');
  const [radiologistOptions, setRadiologistOptions] = useState<any[]>([{ label: 'All Radiologists', value: 'ALL' }]);
  const visitTypes = [
    { label: 'All Visit Types', value: 'ALL' },
    { label: 'OPD (Outpatient)', value: 'OPD' },
    { label: 'IPD (Inpatient)', value: 'IPD' },
    { label: 'Emergency', value: 'Emergency' }
  ];
  const dummyModalityLocations = [
    { label: 'All Locations', value: 'ALL' },
    { label: 'Main Building - Ground Floor', value: 'LOC1' },
    { label: 'Annex Building - 1st Floor', value: 'LOC2' },
  ];
  // Condition helpers
  const needsModality = ['MIS Master Report', 'Modality Based Report'].includes(activeReport);
  const needsManagementFilters = activeReport === 'Management Report';
  const needsModalityLocation = activeReport === 'Modality Based Report';

  // Fetch centers and radiologists on mount
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const payload: any = {
          limit: 1000,
          page: 0
        };

        const response = await makePostCall('/pacs-list', payload);
        if (response?.data?.success && response?.data?.data) {
          const uniqueSites = new Set<string>();
          response.data.data.forEach((study: any) => {
            const site = study.pacs_order?.po_site;
            if (site) {
              uniqueSites.add(site);
            }
          });

          const options = [
             { label: 'All Centers', value: 'ALL' },
             ...Array.from(uniqueSites).map((site: string) => ({
               label: site,
               value: site,
             })),
           ];
          setCenters(options as any);
        }
      } catch (error) {
        console.error('Error fetching centers from pacs-list:', error);
      }
    };

    const fetchRadiologists = async () => {
      try {
        const response = await makeGetCall('/get-rad-users');
        if (response?.data?.success && response?.data?.data) {
          const options = [
            { label: 'All Radiologists', value: 'ALL' },
            ...response.data.data.map((user: any) => ({
              label: user.user_fullname || user.username,
              value: user.username,
            }))
          ];
          setRadiologistOptions(options);
        }
      } catch (error) {
        console.error('Error fetching radiologists:', error);
      }
    };

    fetchCenters();
    fetchRadiologists();
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // --- Helpers ---
  const openFilterModal = (reportType: string) => {
    setActiveReport(reportType);
    setIsFilterModalVisible(true);
  };

  const resetStatusState = useCallback(() => {
    setMisStatus('pending');
    setRequestId('');
    setQueueId('');
    setWaitSeconds(30);
    setDownloadUrl('');
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startCountdown = useCallback((seconds: number) => {
    setWaitSeconds(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setWaitSeconds(prev => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const pollForStatus = useCallback((reqId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const response = await makePostCall('/mis-report-status', { request_id: reqId });
        const data = response?.data;

        if (data?.success && data?.status === 'completed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (countdownRef.current) clearInterval(countdownRef.current);
          pollingRef.current = null;
          countdownRef.current = null;
          setDownloadUrl(data.download_url || '');
          setMisStatus('done');
        } else if (data?.status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (countdownRef.current) clearInterval(countdownRef.current);
          pollingRef.current = null;
          countdownRef.current = null;
          setMisStatus('error');
          message.error(data?.message || 'MIS report generation failed.');
        }
        // else still pending — keep polling
      } catch (err) {
        console.error('MIS status poll error:', err);// Don't stop polling on transient errors
      }
    }, 5000); // poll every 5 seconds
  }, []);

  // --- Generate handler ---
  const handleGenerate = async () => {
    setIsFilterModalVisible(false);
    resetStatusState();
    setIsStatusModalVisible(true);
    try {
      const payload: any = {
        report_type: activeReport,
        user_id: getUserDetails()?.username,
      };

      if (dateRange && dateRange.length === 2) {
        if (activeReport === 'Management Report') {
          payload.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD HH:mm');
          payload.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD HH:mm');
        } else {
          payload.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
          payload.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
        }
      }
      if (selectedCenter && selectedCenter !== 'ALL') {
        payload.center_id = selectedCenter;
      }
      if (['Technician Report', 'Radiologist Report', 'MIS Master Report', 'Critical Finding MIS', 'Modality Based Report'].includes(activeReport) && selectedModality && selectedModality !== 'ALL') {
        payload.modality = selectedModality;
      }
      if (activeReport === 'Management Report') {
        if (selectedRadiologist !== 'ALL') payload.radiologist = selectedRadiologist;
        if (selectedVisitType !== 'ALL') payload.visit_type = selectedVisitType;
      }
      if (activeReport === 'Modality Based Report' && selectedModalityLocation !== 'ALL') {
        payload.modality_location = selectedModalityLocation;
      }

      const response = await makePostCall('/generate-mis-report', payload);
      const data = response?.data;

      if (data?.success) {
        const reqId = data.request_id || '-';
        const queId = data.queue_id || '-';
        const estimatedSeconds = data.estimated_seconds || 30;

        setRequestId(reqId);
        setQueueId(queId);
        startCountdown(estimatedSeconds);
        pollForStatus(reqId);
      } else {
        setMisStatus('error');
        message.error(data?.message || 'Failed to submit MIS report request.');
      }
    } catch (err: any) {
      console.error('MIS generate error:', err);
      setMisStatus('error');
      message.error(err?.response?.data?.message || err.message || 'Failed to submit MIS report request.');
    }
  };
  // --- Download handler ---
  const handleDownload = async () => {
    if (!downloadUrl) return;
    try {
      const response = await makeGetCallForDownload(downloadUrl);

      let filename = 'MIS_Report.xlsx';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition && contentDisposition.includes('filename=')) {
        const matches = contentDisposition.match(/filename="?([^"]+)"?/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const extMatch = filename.match(/(\.[^.]+)$/);
      const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');
      const ext = extMatch ? extMatch[1] : '.xlsx';

      if (activeReport === 'Radiologist Report') {
        filename = `RADIOLOGIST_REPORT_${endDate}${ext}`;
      } else if (activeReport === 'MIS Master Report') {
        const dateStr = dayjs().format('DD_MM_YYYY');
        filename = `MIS_MASTER_REPORT_${dateStr}${ext}`;
      } else if (activeReport === 'Technician Report') {
        filename = `MIS_CATEGORY_TECHNICIAN_REPORT_${endDate}${ext}`;
      } else if (activeReport === 'Critical Finding MIS') {
        filename = `MIS_CATEGORY_CRITICAL_FINDING_${endDate}${ext}`;
      } else if (activeReport === 'Management Report') {
        filename = `MIS_CATEGORY_MANAGEMENT_REPORT_${endDate}${ext}`;
      } else if (activeReport === 'Business Report') {
        filename = `MIS_CATEGORY_BILLING_${endDate}${ext}`;
      } else if (activeReport === 'Modality Based Report') {
        filename = `MIS_CATEGORY_MODALITY_REPORT_${endDate}${ext}`;
      } else if (activeReport === 'Scan Report') {
        filename = `MIS_CATEGORY_SCAN_REPORT_${endDate}${ext}`;
      } else if (activeReport === 'Order/Pacs Report') {
        filename = `MIS_ORDERPACS_REPORT_${endDate}${ext}`;
      } else if (activeReport === 'Front office statistics') {
        filename = `MIS_FRONT_OFFICE_STATS_${endDate}${ext}`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsStatusModalVisible(false);
      setIsFilterModalVisible(false);
    } catch (err: any) {
      console.error('Download error:', err);
      message.error(err.message || 'Failed to download the file.');
    }
  };

  const handleCloseStatusModal = () => {
    resetStatusState();
    setIsStatusModalVisible(false);
  };

  const reportTypes = [
    {
      key: 'Technician Report',
      title: 'Technician Report',
    },
    {
      key: 'Radiologist Report',
      title: 'Radiologist Report',
    },
    {
      key: 'MIS Master Report',
      title: 'MIS Master Report',
    },
    {
      key:'Critical Finding MIS',
      title:'Critical Finding MIS',
    },
    {
      key:'Management Report',
      title:'Management Report',
    },
    {
      key:'Business Report',
      title:'Business Report',
    },
    {
      key:'Modality Based Report',
      title:'Modality Based Report',
    },
    {
      key:'Scan Report',
      title:'Scan Report',
    },
    {
      key:'Order/Pacs Report',
      title:'Order/Pacs Report',
    },
    {
      key:'Front office statistics',
      title:'Front office statistics',
    }
  ];

  return (
    <div style={{ background: '#F5F7FD', height: '100%', overflow: 'auto' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>{StyleTabsComponent && StyleTabsComponent()}</div>
      </div>

      <ErrorBoundary>
        <div className="admin-container" style={{ padding: '20px' }}>
          {/* Report type cards */}
          <Row gutter={[24, 24]}>
            {reportTypes.map(report => (
              <Col xs={24} sm={12} md={8} key={report.key}>
                <Card
                  hoverable
                  onClick={() => openFilterModal(report.title)}
                  style={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '8px' }}
                >
                  <Card.Meta
                    avatar={<FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
                    title={report.title}
                    description={report.description}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {/* ─── Filter Modal ─── */}
          <Modal
            title={`Generate ${activeReport}`}
            open={isFilterModalVisible}
            onCancel={() => setIsFilterModalVisible(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsFilterModalVisible(false)}>
                Cancel
              </Button>,
              <Button key="generate" type="primary" onClick={handleGenerate}>
                Generate
              </Button>,
            ]}
          >
            <Space direction="vertical" size="large" style={{ width: '100%', marginTop: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                  {activeReport === 'Critical Finding MIS' ? 'Scan Date Range' : 'Date Range'}
                </label>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={dates => setDateRange(dates)}
                  format={activeReport === 'Management Report' ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'}
                  showTime={activeReport === 'Management Report' ? { format: 'HH:mm' } : false}
                  allowClear={false}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Center</label>
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="Select a center"
                  value={selectedCenter}
                  onChange={value => setSelectedCenter(value)}
                  options={centers}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              {needsModalityLocation && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Modality Location</label>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Select a modality location"
                    value={selectedModalityLocation}
                    onChange={value => setSelectedModalityLocation(value)}
                    options={dummyModalityLocations}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div>
              )}

              {needsModality && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Modality</label>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Select a modality"
                    value={selectedModality}
                    onChange={value => setSelectedModality(value)}
                    options={enhancedModalityOptions}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div>
              )}

              {needsManagementFilters && (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Radiologist</label>
                    <Select
                      showSearch
                      style={{ width: '100%' }}
                      placeholder="Select Radiologist"
                      value={selectedRadiologist}
                      onChange={value => setSelectedRadiologist(value)}
                      options={radiologistOptions}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Visit Type</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select Visit Type"
                      value={selectedVisitType}
                      onChange={value => setSelectedVisitType(value)}
                      options={visitTypes}
                    />
                  </div>
                </>
              )}
            </Space>
          </Modal>

          {/* ─── MIS Status Monitor Modal ─── */}
          <Modal
            title="MIS Status Monitor"
            open={isStatusModalVisible}
            closable={false}
            maskClosable={false}
            footer={[
              <Button key="close" onClick={handleCloseStatusModal}>
                Close
              </Button>,
            ]}
          >
            <div style={{ padding: '16px 0' }}>
              {/* MIS Name + Date range — always shown */}
              <div
                style={{
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                }}
              >
                <Row gutter={[16, 8]}>
                  <Col span={24}>
                    <Text strong>MIS Name: </Text>
                    <Text>{activeReport}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>Start Date: </Text>
                    <Text>{dateRange?.[0] ? dayjs(dateRange[0]).format('DD/MM/YYYY') : '-'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>End Date: </Text>
                    <Text>{dateRange?.[1] ? dayjs(dateRange[1]).format('DD/MM/YYYY') : '-'}</Text>
                  </Col>
                </Row>
              </div>

              {/* Pending state */}
              {misStatus === 'pending' && (
                <div style={{ textAlign: 'center' }}>
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 36, color: '#1890ff' }} spin />}
                    style={{ marginBottom: '20px' }}
                  />
                  <div style={{ marginBottom: '12px' }}>
                    <Text>
                      Your MIS request has been submitted and your request id is:{' '}
                      <Text strong>{requestId || '...'}</Text> and queue id is:{' '}
                      <Text strong>{queueId || '...'}</Text>
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary">
                      Please wait for <Text strong>{waitSeconds}</Text> seconds
                    </Text>
                  </div>
                </div>
              )}

              {/* Done state */}
              {misStatus === 'done' && (
                <div style={{ textAlign: 'center' }}>
                  <CheckCircleOutlined
                    style={{ fontSize: 48, color: '#52c41a', marginBottom: '16px' }}
                  />
                  <div style={{ marginBottom: '12px' }}>
                    <Text>
                      Your MIS has been generated successfully, please{' '}
                      <a
                        onClick={handleDownload}
                        style={{ fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        CLICK HERE
                      </a>{' '}
                      to download
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      Once downloaded, MIS will be removed from server. Please regenerate if required.
                    </Text>
                  </div>
                </div>
              )}

              {/* Error state */}
              {misStatus === 'error' && (
                <div style={{ textAlign: 'center' }}>
                  <CloseOutlined
                    style={{
                      fontSize: 48,
                      color: '#ff4d4f',
                      marginBottom: '16px',
                      border: '3px solid #ff4d4f',
                      borderRadius: '50%',
                      padding: '8px',
                    }}
                  />
                  <div>
                    <Text type="danger">
                      MIS report generation failed. Please close this window and try again.
                    </Text>
                  </div>
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Text type="secondary" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                  (Please do not close this window unless you are done)
                </Text>
              </div>
            </div>
          </Modal>
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default MISDashboardModal;
